// @TODO determine whether to re-use this or style a new modal
import React, {
  useState,
  Dispatch,
  SetStateAction,
  PropsWithChildren,
  useCallback,
  useEffect,
} from 'react';
import { gql } from '@yi/core';

import { useAccessTier } from '~/src/common/subscription';
import { useCurrentUser } from '~/src/common/user';
import { CloseModalButton } from '~/src/components/modals/CloseModalButton';

import { ModalContentWrapper, Modal } from '../elements';
import { SignInForm, ForgotPasswordForm } from '../modalForms';

export type ViewNames =
  | gql.typesClient.MembershipTier
  | 'SignIn'
  | 'ForgotPassword'
  | 'Anon'
  | 'InvalidMembership';
export type View = (s: Dispatch<SetStateAction<ViewNames>>) => React.ReactNode;
type Views = { [k in ViewNames]: undefined | View };

export interface Props extends Omit<React.ComponentProps<typeof Modal>, 'open'> {
  views: Partial<Views>;
  initialView?: keyof Views;
  onClose?: (params: { setView: Dispatch<SetStateAction<ViewNames>> }) => void;
  onViewChanged?: (viewName: ViewNames) => void;
  closable?: boolean;
}

const DEFAULT_VIEW = 'Anon';

export const MultiViewPaywall: React.FC<PropsWithChildren<Props>> = ({
  views: views_,
  initialView = DEFAULT_VIEW,
  onClose,
  children,
  closable = true,
  onViewChanged,
  ...props
}) => {
  const { currentUser, loading } = useCurrentUser();
  const { tier, loading: loadingTier } = useAccessTier();
  const [emailAddress, setEmail] = useState('');

  const [viewKey, setViewKey] = useState<keyof Views>(DEFAULT_VIEW);

  useEffect(() => {
    if (typeof onViewChanged === 'function') {
      onViewChanged(viewKey);
    }
  }, [onViewChanged, viewKey]);

  const views: Views = {
    ForgotPassword: () => <ForgotPasswordForm emailAddress={emailAddress} />,
    SignIn: () => (
      <SignInForm
        setEmail={setEmail}
        closeModal={() => null}
        onClickForgotPassword={() => setViewKey('ForgotPassword')}
      />
    ),
    Anon: undefined,
    Free: undefined,
    Digital: undefined,
    Premium: undefined,
    InvalidMembership: undefined,
    ...views_,
  };

  let view;
  if (currentUser?.membership_is_valid === false && views.InvalidMembership) {
    view = views.InvalidMembership;
  } else if (viewKey !== DEFAULT_VIEW) {
    view = views[viewKey];
  } else {
    view = views[initialView];
  }

  if (!loadingTier && tier !== null && tier !== 'Anon') {
    view = views[tier];
  }

  const hanldeClose = useCallback(() => {
    if (typeof onClose === 'function') {
      onClose({ setView: setViewKey });
    }
  }, [onClose]);

  if (loading || loadingTier || !view) return null;
  return (
    <Modal open={!!view} onClose={hanldeClose} {...props}>
      {closable && <CloseModalButton onClick={hanldeClose} />}
      {children}
      <ModalContentWrapper>{view(setViewKey)}</ModalContentWrapper>
    </Modal>
  );
};
